import { useState, useEffect, useCallback } from 'react'

/**
 * SSR-safe localStorage hook with cross-tab sync.
 *
 * - Reads the stored value on mount (JSON-parsed).
 * - Writes to localStorage on every state update.
 * - Listens to the `storage` event to sync across browser tabs.
 * - Falls back to `initialValue` if the key is missing or parse fails.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Lazy initializer — only runs once on mount
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue
    try {
      const item = window.localStorage.getItem(key)
      return item !== null ? (JSON.parse(item) as T) : initialValue
    } catch {
      return initialValue
    }
  })

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value
        try {
          window.localStorage.setItem(key, JSON.stringify(next))
        } catch {
          // Storage quota exceeded or private browsing — fail silently
        }
        return next
      })
    },
    [key]
  )

  // Sync state when another tab writes to the same key
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key || event.storageArea !== window.localStorage) return
      try {
        const next = event.newValue !== null
          ? (JSON.parse(event.newValue) as T)
          : initialValue
        setStoredValue(next)
      } catch {
        // Malformed JSON from another tab — ignore
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [key, initialValue])

  return [storedValue, setValue]
}
